/* In-Stock Pick List — async CSV generation (task → poll → download).
 * Base: /pharmacy-ims/api/v1/reports (httpSupplier targets …/pharmacy-ims/api/,
 * so paths are v1/reports/…).
 *
 *   POST v1/reports/in-stock/export   { warehouseId, from, to }
 *        -> { taskId, reportType, status: "PENDING", fileName, ... }
 *   GET  v1/reports/tasks/{taskId}     -> poll until status COMPLETED / FAILED
 *   GET  v1/reports/tasks/{taskId}/download -> CSV blob
 *
 * The tasks endpoint is shared across report types, so status/download reuse the
 * same normalization as the Out of Stuck report. This is the single integration
 * point for the pick list — change endpoint paths here if the backend differs. */
import { httpSupplier } from "../../HttpCommonALL";
import { getCurrentUserName } from "../../../Services/warehouseContext";
import { saveAs } from "file-saver";

/* Audit header — resolved per call to the signed-in user. */
const xUser = () => ({ "X-User": getCurrentUserName() });

const BASE = "v1/reports/in-stock";
const TASKS = "v1/reports/tasks";

const unwrap = (r) => {
  const env = r?.data;
  if (env && typeof env === "object" && "success" in env) {
    if (env.success === false) throw new Error(env.message || "Request failed");
    return env.data;
  }
  return env;
};

/* Normalize the async-export task envelope payload:
   { taskId, reportType, status, fileName, rowCount, errorMessage, ... }
   -> { taskId, status: PENDING | READY | FAILED, raw, fileName, rowCount, message }. */
const normalizeTask = (data = {}) => {
  const raw = (data.status ?? data.state ?? "").toString().toUpperCase();
  let status = "PENDING";
  if (["READY", "COMPLETED", "COMPLETE", "SUCCESS", "DONE"].includes(raw)) status = "READY";
  else if (["FAILED", "FAILURE", "ERROR"].includes(raw)) status = "FAILED";
  return {
    taskId: data.taskId ?? data.id ?? data.reportId ?? null,
    status,
    raw,
    fileName: data.fileName ?? data.filename ?? null,
    rowCount: data.rowCount ?? null,
    message: data.errorMessage ?? data.message ?? null,
  };
};

const pickListService = {
  /* POST v1/reports/in-stock/export -> start pick-list CSV generation.
     Body: { warehouseId, from, to } where from/to are ISO datetimes
     (YYYY-MM-DDTHH:mm:ss). Returns the normalized task so the caller can poll
     by taskId and name the download with the server-provided fileName. */
  requestExport({ warehouseId, from, to } = {}) {
    return httpSupplier
      .post(`${BASE}/export`, { warehouseId, from, to }, { headers: xUser() })
      .then((r) => normalizeTask(unwrap(r)));
  },

  /* GET v1/reports/tasks/{taskId} -> the task object, polled until COMPLETED / FAILED. */
  exportStatus(taskId) {
    return httpSupplier.get(`${TASKS}/${taskId}`, { headers: xUser() }).then((r) => normalizeTask(unwrap(r)));
  },

  /* GET v1/reports/tasks/{taskId}/download -> CSV blob; saves to disk. */
  download(taskId, filename = "in-stock-pick-list.csv") {
    return httpSupplier
      .get(`${TASKS}/${taskId}/download`, { responseType: "blob", headers: xUser() })
      .then((r) => {
        saveAs(new Blob([r.data], { type: "text/csv;charset=utf-8" }), filename);
      });
  },
};

export default pickListService;
